import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Settings, RefreshCw, Wrench, Edit, Trash2, Pencil } from 'lucide-react';
import axios from 'axios';
import { Button, FormSection, AlertMessage, SimCardBadge } from '../../common/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDateTime } from '../../../utils/dateUtils';
import { computeFallbackValues } from '../../../utils/legacyFallbackMappings';
import { getSimCardOwner, isIccidAttribute } from '../../../utils/simCardUtils';
import { serialMatchesSearch } from '../../../utils/searchNormalize';

const parseDateToMs = (value) => {
  if (!value) return null;
  const date = new Date(value);
  const ms = date.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const DeviceModules = ({ 
  repairData, 
  onModuleAction = null,
  showActions = false,
  showEditButton = true, // Control Edit button visibility separately
  onRepairSaved = null, // Callback to refresh activity after repair is saved
  productConfig = null, // Product configuration with assignedModules and moduleAttributesMap
  deviceStatus = 'active', // Device status for validation
  onAppendResolution = null, // Callback to append text to final resolution
  deviceAttributeValues: deviceAttributeValuesProp = null // Product-attribute values for legacy fallback
}) => {
  const { user } = useAuth();
  const [deviceModules, setDeviceModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);
  const [fallbackModuleValues, setFallbackModuleValues] = useState({}); // Legacy fallback values for display only
  
  // Module repairs state
  const [moduleRepairs, setModuleRepairs] = useState({}); // { moduleName: [repairs] }
  const [moduleExchanges, setModuleExchanges] = useState({}); // { moduleName: [exchanges] }
  const [activeRepairModule, setActiveRepairModule] = useState(null);
  const [repairFormData, setRepairFormData] = useState({
    repair_description: '',
    repair_type: ''
  });
  const [repairSaving, setRepairSaving] = useState(false);
  const [repairError, setRepairError] = useState('');
  const [repairSuccess, setRepairSuccess] = useState('');
  
  // Module exchange state
  const [activeExchangeModule, setActiveExchangeModule] = useState(null);
  const [exchangeFormData, setExchangeFormData] = useState({
    exchange_description: '',
    repair_type: ''
  });
  const [exchangeSaving, setExchangeSaving] = useState(false);
  const [exchangeError, setExchangeError] = useState('');
  const [moduleAttributesDefinition, setModuleAttributesDefinition] = useState({}); // Module attribute definitions (map: moduleId -> attributes array) - for display
  const [exchangeAttributesDefinition, setExchangeAttributesDefinition] = useState([]); // Module attribute definitions for exchange form
  const [moduleAttributeValues, setModuleAttributeValues] = useState({}); // Values for module attributes (display existing values)
  const [exchangeAttributeValues, setExchangeAttributeValues] = useState({}); // Values for new module attributes during exchange
  
  // Motherboard exchange mode state
  const [exchangeMode, setExchangeMode] = useState('from_retired'); // 'from_retired' | 'new_motherboard'
  const [retiredDonors, setRetiredDonors] = useState([]);
  const [retiredDonorsLoading, setRetiredDonorsLoading] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null); // Selected retired donor device
  const [deviceProduct, setDeviceProduct] = useState(null); // Serviced device's product, for cross-product donor warning
  const [newSerialNumber, setNewSerialNumber] = useState('');
  const [replacedDeviceStatus, setReplacedDeviceStatus] = useState(null); // 'retired' | 'wasted'
  const [donorSearchTerm, setDonorSearchTerm] = useState('');
  const [donorDropdownOpen, setDonorDropdownOpen] = useState(false);
  const donorSearchRef = useRef(null);
  const donorDropdownRef = useRef(null);

  // Donor picker filter. Serial matching ignores formatting, so "23-3142" also
  // finds "NAV-233142" and vice versa.
  const filteredRetiredDonors = retiredDonors.filter(d => {
    if (!donorSearchTerm.trim()) return true;
    return serialMatchesSearch(d.serial_number, donorSearchTerm) ||
      (d.product_name || '').toLowerCase().includes(donorSearchTerm.trim().toLowerCase());
  });

  // Module edit state
  const [activeEditModule, setActiveEditModule] = useState(null);
  const [moduleEditValues, setModuleEditValues] = useState({}); // Values for editing module attributes
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [moduleIdMap, setModuleIdMap] = useState({}); // Map module names to module IDs
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, type, moduleName }
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editHistory, setEditHistory] = useState(null); // { id, type, moduleName, description, repair_type }
  const [editHistorySaving, setEditHistorySaving] = useState(false);
  const [editHistoryError, setEditHistoryError] = useState('');

  // Build module ID map from productConfig when available
  useEffect(() => {
    if (productConfig && productConfig.assignedModules) {
      const idMap = {};
      productConfig.assignedModules.forEach(module => {
        idMap[module.name] = module.id.toString();
      });
      setModuleIdMap(idMap);
      
      // Also set module attributes definition from productConfig
      if (productConfig.moduleAttributesMap) {
        setModuleAttributesDefinition(productConfig.moduleAttributesMap);
      }
    }
  }, [productConfig]);

  // Recompute fallback values when prop-based device attribute values change
  useEffect(() => {
    if (deviceAttributeValuesProp && productConfig) {
      setFallbackModuleValues(
        computeFallbackValues(productConfig, deviceAttributeValuesProp, moduleAttributeValues)
      );
    }
  }, [deviceAttributeValuesProp, productConfig, moduleAttributeValues]);

  // Fetch device modules when device_id is available
  useEffect(() => {
    if (repairData.device_id) {
      fetchDeviceModules(repairData.device_id);
    } else if (productConfig && productConfig.assignedModules && productConfig.assignedModules.length > 0) {
      // No device_id but we have productConfig from parent - show product modules
      const modules = productConfig.assignedModules.map(module => ({
        module_type: module.name,
        supplier: null,
        production_batch: null,
        created_at: null,
        updated_at: null
      }));
      setDeviceModules(modules);
      
      // Set module attributes definition if available
      if (productConfig.moduleAttributesMap) {
        setModuleAttributesDefinition(productConfig.moduleAttributesMap);
      }
      
      setLoadingModules(false);
    } else {
      setLoadingModules(false);
    }
  }, [repairData.device_id, productConfig]);

  // Close donor dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        donorDropdownRef.current && !donorDropdownRef.current.contains(e.target) &&
        donorSearchRef.current && !donorSearchRef.current.contains(e.target)
      ) {
        setDonorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchModuleHistory = useCallback(async () => {
    if (!repairData.device_id) return;

    const deviceId = repairData.device_id;
    const currentRepairId = repairData.id ? Number(repairData.id) : null;
    const repairWindowStart =
      parseDateToMs(repairData.started_date) ??
      parseDateToMs(repairData.reported_date) ??
      null;
    const repairWindowEnd =
      parseDateToMs(repairData.completed_date) ??
      null;

    const shouldIncludeEntry = (entryRepairId, entryTimestampMs) => {
      // If we have a current repair, prefer matching on repair_id,
      // but fall back to a time window when repair_id is missing.
      if (currentRepairId) {
        if (entryRepairId) {
          return entryRepairId === currentRepairId;
        }
        if (repairWindowStart === null || entryTimestampMs === null) {
          return false;
        }
        const endBound = repairWindowEnd ?? Date.now();
        return entryTimestampMs >= repairWindowStart && entryTimestampMs <= endBound;
      }

      // On device view (no current repair), include all entries that have
      // any repair_id (we want the full history across repairs).
      if (entryRepairId) {
        return true;
      }

      // Entries without repair_id are likely legacy and can be omitted
      // from the aggregated device view for now.
      return false;
    };

    const normalizeEntries = (entries) => {
      const list = Array.isArray(entries) ? entries : [];
      return list.reduce((acc, entry) => {
        let details = entry.details;
        if (typeof details === 'string') {
          try {
            details = JSON.parse(details);
          } catch {
            details = {};
          }
        }
        if (!details || typeof details !== 'object') {
          details = {};
        }

        const moduleName = details.module_name || 'Unknown Module';
        const entryRepairId = details.repair_id !== undefined && details.repair_id !== null
          ? Number(details.repair_id)
          : null;
        const entryTimestampMs = parseDateToMs(entry.timestamp);
        if (!shouldIncludeEntry(entryRepairId, entryTimestampMs)) {
          return acc;
        }

        acc.push({
          ...entry,
          details,
          moduleName
        });
        return acc;
      }, []);
    };

    const groupEntriesByModule = (entries) => {
      const grouped = entries.reduce((acc, entry) => {
        if (!acc[entry.moduleName]) {
          acc[entry.moduleName] = [];
        }
        acc[entry.moduleName].push(entry);
        return acc;
      }, {});

      Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => {
          const aTime = parseDateToMs(a.timestamp) ?? 0;
          const bTime = parseDateToMs(b.timestamp) ?? 0;
          return bTime - aTime;
        });
      });

      return grouped;
    };

    try {
      const [repairsResponse, exchangesResponse] = await Promise.all([
        axios.get(`/api/devices/${deviceId}/module-repairs`),
        axios.get(`/api/devices/${deviceId}/module-exchanges`)
      ]);

      setModuleRepairs(groupEntriesByModule(normalizeEntries(repairsResponse.data)));
      setModuleExchanges(groupEntriesByModule(normalizeEntries(exchangesResponse.data)));
    } catch (err) {
      console.error('Error fetching module history:', err);
    }
  }, [
    repairData.device_id,
    repairData.id,
    repairData.started_date,
    repairData.reported_date,
    repairData.completed_date
  ]);

  useEffect(() => {
    if (repairData.device_id) {
      fetchModuleHistory();
    }
  }, [repairData.device_id, fetchModuleHistory]);

  const fetchDeviceModules = async (deviceId) => {
    try {
      setLoadingModules(true);
      const deviceResponse = await axios.get(`/api/devices/${deviceId}`);
      const device = deviceResponse.data;
      
      // Get product configuration (use prop if available, otherwise fetch it)
      let configToUse = productConfig;
      if (!configToUse || !configToUse.assignedModules) {
        // Prefer product_name over model
        const productName = device.product_name || device.model;
        if (!productName) {
          console.error('No product name or model found for device');
          setDeviceModules([]);
          setLoadingModules(false);
          return;
        }
        try {
          const productConfigResponse = await axios.get(`/api/devices/product-config/${encodeURIComponent(productName)}`);
          configToUse = productConfigResponse.data;
        } catch (productErr) {
          console.error('Error fetching product configuration:', productErr);
          setDeviceModules([]);
          return;
        }
      }
      
      // Always show modules from product configuration, merged with device-specific data
      if (configToUse && configToUse.assignedModules) {
        // Create a map of device modules for quick lookup
        const deviceModulesMap = {};
        if (device.modules && device.modules.length > 0) {
          device.modules.forEach(dm => {
            deviceModulesMap[dm.module_type] = dm;
          });
        }
        
        // Transform product modules, overlaying device-specific data when available
        const mergedModules = configToUse.assignedModules.map(module => {
          const deviceModule = deviceModulesMap[module.name];
          
          // If device has specific module data, use it; otherwise use placeholder
          if (deviceModule) {
            return {
              module_type: deviceModule.module_type,
              supplier: deviceModule.supplier,
              production_batch: deviceModule.production_batch,
              created_at: deviceModule.created_at,
              updated_at: deviceModule.updated_at
            };
          } else {
            // No device-specific data, show placeholder
            return {
              module_type: module.name,
              supplier: null,
              production_batch: null,
              created_at: null,
              updated_at: null
            };
          }
        });
        
        setDeviceModules(mergedModules);
        
        // Load module attributes for the device
        const allModuleAttributes = {};
        if (device.moduleAttributes && device.moduleAttributes.length > 0) {
          device.moduleAttributes.forEach(attr => {
            const key = `${attr.module_id}_${attr.attribute_name}`;
            allModuleAttributes[key] = attr.attribute_value;
          });
          setModuleAttributeValues(allModuleAttributes);
        }

        // Compute legacy fallback values for display
        const devAttrValues = deviceAttributeValuesProp || {};
        // If prop not provided, parse from fetched device data
        const effectiveDevAttrValues = Object.keys(devAttrValues).length > 0
          ? devAttrValues
          : (device.attributes || []).reduce((map, a) => { map[a.name] = a.value; return map; }, {});
        setFallbackModuleValues(
          computeFallbackValues(configToUse, effectiveDevAttrValues, allModuleAttributes)
        );
        
        // Build module attributes definition map (only if not already set from prop)
        if (!productConfig || !productConfig.moduleAttributesMap) {
          const moduleAttrsMap = {};
          Object.keys(configToUse.moduleAttributesMap || {}).forEach(moduleId => {
            const attrs = configToUse.moduleAttributesMap[moduleId];
            moduleAttrsMap[moduleId] = attrs;
          });
          setModuleAttributesDefinition(moduleAttrsMap);
        }
      } else {
        setDeviceModules([]);
      }
    } catch (err) {
      console.error('Error fetching device modules:', err);
      setDeviceModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const handleModuleAction = (action, module) => {
    if (action === 'repair') {
      handleOpenRepairForm(module.module_type);
    } else if (action === 'exchange') {
      handleOpenExchangeForm(module);
    } else if (action === 'edit') {
      handleOpenEditForm(module);
    }
  };

  // Helper to format resolution text
  const formatResolutionText = (moduleName, description, type, action = 'repair') => {
    const suffix = action === 'exchange' ? ' exchanged' : ' repaired';
    return `${moduleName}${suffix}`;
  };

  // Module repair handlers

  const handleOpenRepairForm = (moduleName) => {
    console.log('Opening repair form for module:', moduleName);
    console.log('Device ID:', repairData.device_id);
    fetchModuleHistory();
    setActiveRepairModule(moduleName);
    setRepairFormData({
      repair_description: '',
      repair_type: ''
    });
    setRepairError('');
    setRepairSuccess('');
  };

  const handleCloseRepairForm = () => {
    setActiveRepairModule(null);
    setRepairFormData({
      repair_description: '',
      repair_type: ''
    });
    setRepairError('');
    setRepairSuccess('');
  };

  const handleRepairFormChange = (field, value) => {
    setRepairFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveRepair = async (moduleName) => {
      // Validation
    if (!repairFormData.repair_type) {
      setRepairError('Please select a repair type');
      return;
    }

    try {
      setRepairSaving(true);
      setRepairError('');

      await axios.post(`/api/devices/${repairData.device_id}/modules/${moduleName}/repair`, {
        repair_description: repairFormData.repair_description,
        repair_type: repairFormData.repair_type,
        performed_by: user.id,
        repair_id: repairData.id // Pass repair ID to create activity entry
      });

      // Refresh module history
      await fetchModuleHistory();
      
      // Close the form
      setActiveRepairModule(null);
      
      // Clear form
      setRepairFormData({
        repair_description: '',
        repair_type: ''
      });
      
      setRepairSuccess(`${moduleName} repair recorded successfully`);
      setTimeout(() => setRepairSuccess(''), 3000);
      
      // Auto-update resolution (must complete before fetchRepair to avoid race condition)
      if (onAppendResolution) {
        const text = formatResolutionText(
          moduleName, 
          repairFormData.repair_description, 
          repairFormData.repair_type,
          'repair'
        );
        await onAppendResolution(text);
      }

      // Notify parent to refresh activity
      if (onRepairSaved) {
        onRepairSaved();
      }
    } catch (err) {
      console.error('Error saving module repair:', err);
      setRepairError(err.response?.data?.error || 'Failed to save repair');
    } finally {
      setRepairSaving(false);
    }
  };

  // Module exchange handlers
  const fetchModuleAttributesDefinition = async (moduleName) => {
    try {
      // First get all modules from catalog
      const modulesResponse = await axios.get('/api/modules');
      const modules = modulesResponse.data;
      
      // Find the module by name
      const targetModule = modules.find(m => m.name === moduleName);
      
      if (!targetModule) {
        console.warn('Module not found in catalog:', moduleName);
        return [];
      }
      
      // Get module with attributes
      const moduleResponse = await axios.get(`/api/modules/${targetModule.id}`);
      return moduleResponse.data.attributes || [];
    } catch (err) {
      console.error('Error fetching module attributes definition:', err);
      return [];
    }
  };


  const handleOpenExchangeForm = async (module) => {
    const moduleName = module.module_type || 'Unknown Module';
    console.log('Opening exchange form for module:', moduleName);
    console.log('Device ID:', repairData.device_id);
    
    fetchModuleHistory();
    setActiveExchangeModule(moduleName);
    setExchangeFormData({
      exchange_description: '',
      repair_type: ''
    });
    setExchangeError('');
    setExchangeAttributeValues({}); // Clear exchange attribute values, not the display values
    setExchangeMode('from_retired');
    setSelectedDonor(null);
    setNewSerialNumber('');
    setReplacedDeviceStatus(null);
    setDonorSearchTerm('');
    setDonorDropdownOpen(false);
    setRetiredDonors([]);
    
    // Fetch module attributes definition for the exchange form
    const attributes = await fetchModuleAttributesDefinition(moduleName);
    setExchangeAttributesDefinition(attributes);

    // Pre-populate attribute values with defaults where defined
    const defaults = {};
    for (const attr of attributes) {
      if (attr.default_value) {
        defaults[attr.name] = attr.default_value;
      }
    }
    if (Object.keys(defaults).length > 0) {
      setExchangeAttributeValues(defaults);
    }

    // For Motherboard exchanges, fetch retired donors
    if (moduleName.toLowerCase().includes('motherboard') && repairData.device_id) {
      fetchRetiredDonors();
    }
  };

  const fetchRetiredDonors = async () => {
    if (!repairData.device_id) return;
    try {
      setRetiredDonorsLoading(true);
      // Donors of all products are listed; the device's product_id only orders
      // same-product donors first and drives the cross-product warning.
      const deviceResponse = await axios.get(`/api/devices/${repairData.device_id}`);
      const productId = deviceResponse.data.product_id;
      setDeviceProduct(productId ? { id: productId, name: deviceResponse.data.product_name || deviceResponse.data.model } : null);
      const response = await axios.get('/api/devices/retired-donors', {
        params: productId ? { product_id: productId } : {}
      });
      setRetiredDonors(response.data.devices || []);
    } catch (err) {
      console.error('Error fetching retired donors:', err);
    } finally {
      setRetiredDonorsLoading(false);
    }
  };

  const handleSelectDonor = async (donor) => {
    setSelectedDonor(donor);
    // Auto-populate motherboard attributes from the donor device
    if (donor && repairData.device_id) {
      try {
        const response = await axios.get(`/api/devices/${donor.id}/module-attributes-by-name/${activeExchangeModule}`);
        const attrs = response.data.attributes || {};
        // Ensure serial number attribute is set from donor's serial
        const serialAttr = exchangeAttributesDefinition.find(a => a.name.toLowerCase().includes('serial number'));
        if (serialAttr) {
          attrs[serialAttr.name] = donor.serial_number;
        }
        setExchangeAttributeValues(attrs);
      } catch (err) {
        console.error('Error fetching donor module attributes:', err);
      }
    }
  };

  const handleExchangeModeChange = (mode) => {
    setExchangeMode(mode);
    setSelectedDonor(null);
    setNewSerialNumber('');
    setReplacedDeviceStatus(null);
    setDonorSearchTerm('');
    setDonorDropdownOpen(false);
    setExchangeAttributeValues({});
  };
    

  const handleCloseExchangeForm = () => {
    setActiveExchangeModule(null);
    setExchangeFormData({
      exchange_description: '',
      repair_type: ''
    });
    setExchangeError('');
    setExchangeAttributesDefinition([]); // Clear exchange form's attribute definitions
    setExchangeAttributeValues({}); // Clear exchange attribute values, not the display values
    setExchangeMode('from_retired');
    setSelectedDonor(null);
    setNewSerialNumber('');
    setReplacedDeviceStatus(null);
    setDonorSearchTerm('');
    setDonorDropdownOpen(false);
    setRetiredDonors([]);
  };

  const handleExchangeFormChange = (field, value) => {
    setExchangeFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModuleAttributeChange = (attributeName, value) => {
    setExchangeAttributeValues(prev => ({
      ...prev,
      [attributeName]: value
    }));
  };

  const handleSaveExchange = async (moduleName) => {
    // Validation
    if (!exchangeFormData.repair_type) {
      setExchangeError('Please select a repair type');
      return;
    }

    // Mode-specific validation
    const isMotherboard = moduleName.toLowerCase().includes('motherboard');
    if (isMotherboard && exchangeMode === 'from_retired' && !selectedDonor) {
      setExchangeError('Please select a retired device as the motherboard source');
      return;
    }
    if (isMotherboard && exchangeMode === 'new_motherboard' && !newSerialNumber.trim()) {
      setExchangeError('Please enter the new serial number');
      return;
    }
    if (isMotherboard && !replacedDeviceStatus) {
      setExchangeError('Please select the status for the old motherboard (Retired or Trashed)');
      return;
    }

    // Validate required module attributes (skip for from_retired mode since they're auto-populated)
    const missingAttributes = [];
    for (const attr of exchangeAttributesDefinition) {
      if (attr.required && !attr.hidden) {
        // Check exchangeAttributeValues first, then fall back to default_value for dropdowns
        const value = exchangeAttributeValues[attr.name] || (attr.type === 'dropdown' ? attr.default_value : '');
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          missingAttributes.push(attr.name);
        }
      }
    }
    
    if (missingAttributes.length > 0) {
      setExchangeError(`The following required fields must be filled: ${missingAttributes.join(', ')}`);
      return;
    }

    try {
      setExchangeSaving(true);
      setExchangeError('');

      // Merge exchangeAttributeValues with default values for dropdown attributes
      const attributesToSend = { ...exchangeAttributeValues };
      exchangeAttributesDefinition.forEach(attr => {
        // If attribute doesn't have a value but has a default_value (dropdown), use the default
        if (!attributesToSend[attr.name] && attr.type === 'dropdown' && attr.default_value) {
          attributesToSend[attr.name] = attr.default_value;
        }
      });

      const payload = {
        exchange_description: exchangeFormData.exchange_description,
        repair_type: exchangeFormData.repair_type,
        performed_by: user.id,
        repair_id: repairData.id,
        module_attributes: attributesToSend
      };

      // Add mode-specific fields
      if (isMotherboard && exchangeMode === 'from_retired' && selectedDonor) {
        payload.source_device_id = selectedDonor.id;
      } else if (isMotherboard && exchangeMode === 'new_motherboard' && newSerialNumber.trim()) {
        payload.new_serial_number = newSerialNumber.trim();
      }
      if (isMotherboard && replacedDeviceStatus) {
        payload.replaced_device_status = replacedDeviceStatus;
      }

      const response = await axios.post(`/api/devices/${repairData.device_id}/modules/${moduleName}/exchange`, payload);

      // Refresh module history
      await fetchModuleHistory();
      
      // Refresh device modules to get updated attributes
      await fetchDeviceModules(repairData.device_id);
      
      // Close the form and clear state
      setActiveExchangeModule(null);
      setExchangeFormData({
        exchange_description: '',
        repair_type: ''
      });
      setExchangeAttributeValues({});
      setExchangeAttributesDefinition([]);
      setExchangeMode('from_retired');
      setSelectedDonor(null);
      setNewSerialNumber('');
      setReplacedDeviceStatus(null);
      setDonorSearchTerm('');
      setDonorDropdownOpen(false);
      setRetiredDonors([]);
      
      // Build success message
      let successMsg = `${moduleName} exchange recorded successfully`;
      if (response.data.serial_changed) {
        successMsg += ` • Serial changed from ${response.data.old_serial} to ${response.data.new_serial}`;
      }
      setRepairSuccess(successMsg);
      setTimeout(() => setRepairSuccess(''), 5000);
      
      // Auto-update resolution (must complete before fetchRepair to avoid race condition)
      if (onAppendResolution) {
        let resolutionText = formatResolutionText(
          moduleName, 
          exchangeFormData.exchange_description, 
          exchangeFormData.repair_type,
          'exchange'
        );
        if (response.data.serial_changed) {
          resolutionText += ` (serial: ${response.data.old_serial} → ${response.data.new_serial})`;
        }
        await onAppendResolution(resolutionText);
      }

      // Notify parent to refresh activity and device data (important when serial changed)
      if (onRepairSaved) {
        onRepairSaved();
      }
    } catch (err) {
      console.error('Error saving module exchange:', err);
      setExchangeError(err.response?.data?.error || 'Failed to save exchange');
    } finally {
      setExchangeSaving(false);
    }
  };

  // Module edit handlers
  const handleOpenEditForm = (module) => {
    const moduleName = module.module_type || 'Unknown Module';
    const moduleId = moduleIdMap[moduleName];
    
    if (!moduleId) {
      console.error('Module ID not found for:', moduleName);
      setEditError('Module configuration not found');
      return;
    }
    
    setActiveEditModule(moduleName);
    setEditError('');
    
    // Initialize edit values from current module attribute values
    // For dropdown attributes, use default_value if no saved value exists
    const initialValues = {};
    const moduleAttrs = moduleAttributesDefinition[moduleId] || [];
    moduleAttrs.forEach(attr => {
      if (!attr.hidden) {
        const key = `${moduleId}_${attr.name}`;
        const savedValue = moduleAttributeValues[key];
        if (savedValue) {
          initialValues[key] = savedValue;
        } else if (attr.type === 'dropdown' && attr.default_value) {
          // Use default value for dropdowns when no saved value exists
          initialValues[key] = attr.default_value;
        } else {
          initialValues[key] = '';
        }
      }
    });
    setModuleEditValues(initialValues);
  };

  const handleCloseEditForm = () => {
    setActiveEditModule(null);
    setModuleEditValues({});
    setEditError('');
  };

  const handleModuleEditValueChange = (key, value) => {
    setModuleEditValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveModuleAttributes = async (moduleName) => {
    const moduleId = moduleIdMap[moduleName];
    
    if (!moduleId) {
      setEditError('Module ID not found');
      return;
    }
    
    const moduleAttrs = moduleAttributesDefinition[moduleId] || [];
    
    // Validate required fields if device is not in draft status
    if (deviceStatus !== 'draft') {
      const missingAttributes = [];
      for (const attr of moduleAttrs) {
        if (attr.required && !attr.hidden) {
          const key = `${moduleId}_${attr.name}`;
          const value = moduleEditValues[key];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            missingAttributes.push(attr.name);
          }
        }
      }
      
      if (missingAttributes.length > 0) {
        setEditError(`The following required fields must be filled: ${missingAttributes.join(', ')}`);
        return;
      }
    }
    
    try {
      setEditSaving(true);
      setEditError('');
      
      // Save each attribute individually
      for (const attr of moduleAttrs) {
        if (!attr.hidden) {
          const key = `${moduleId}_${attr.name}`;
          const value = moduleEditValues[key] || '';
          
          await axios.post(`/api/devices/${repairData.device_id}/module-attributes`, {
            module_id: moduleId,
            attribute_name: attr.name,
            attribute_value: value
          });
          
          // Update moduleAttributeValues state
          setModuleAttributeValues(prev => ({
            ...prev,
            [key]: value
          }));
        }
      }
      
      // Close the form
      setActiveEditModule(null);
      setModuleEditValues({});
      
      // Notify parent to refresh activity if callback exists
      if (onRepairSaved) {
        onRepairSaved();
      }
      
      // Show success message
      setRepairSuccess(`${moduleName} attributes saved successfully`);
      setTimeout(() => setRepairSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving module attributes:', err);
      setEditError(err.response?.data?.error || 'Failed to save module attributes');
    } finally {
      setEditSaving(false);
    }
  };

  // Edit the comment / repair type of an existing module repair or exchange record
  const handleOpenHistoryEdit = (item, details, moduleName) => {
    const isExchange = item.actionType === 'exchange';
    setEditHistory({
      id: item.id,
      type: item.actionType,
      moduleName,
      description: (isExchange ? details.exchange_description : details.repair_description) || '',
      repair_type: details.repair_type || ''
    });
    setEditHistoryError('');
  };

  const handleCloseHistoryEdit = () => {
    setEditHistory(null);
    setEditHistoryError('');
  };

  const handleSaveHistoryEdit = async () => {
    if (!editHistory) return;
    if (!editHistory.repair_type) {
      setEditHistoryError('Please select a repair type');
      return;
    }

    try {
      setEditHistorySaving(true);
      setEditHistoryError('');

      await axios.put(`/api/devices/${repairData.device_id}/modules/history/${editHistory.id}`, {
        description: editHistory.description,
        repair_type: editHistory.repair_type
      });

      await fetchModuleHistory();

      if (onRepairSaved) {
        onRepairSaved();
      }

      const actionLabel = editHistory.type === 'exchange' ? 'exchange' : 'repair';
      setRepairSuccess(`${editHistory.moduleName} ${actionLabel} record updated`);
      setTimeout(() => setRepairSuccess(''), 3000);
      setEditHistory(null);
    } catch (err) {
      console.error('Error updating module record:', err);
      setEditHistoryError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setEditHistorySaving(false);
    }
  };

  // Delete module repair/exchange handler
  const handleDeleteModuleHistory = async () => {
    if (!deleteConfirm) return;
    
    const { id, type, moduleName, legacy } = deleteConfirm;

    try {
      setDeleteLoading(true);

      await axios.delete(`/api/devices/${repairData.device_id}/modules/history/${id}`, {
        params: legacy ? { source: 'activity' } : undefined
      });
      
      // Refresh module history
      await fetchModuleHistory();
      
      // Notify parent to refresh activity if callback exists
      if (onRepairSaved) {
        onRepairSaved();
      }
      
      // Show success message
      const actionLabel = type === 'exchange' ? 'exchange' : 'repair';
      setRepairSuccess(`${moduleName} ${actionLabel} record deleted successfully`);
      setTimeout(() => setRepairSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting module history:', err);
      setRepairError(err.response?.data?.error || 'Failed to delete record');
      setTimeout(() => setRepairError(''), 5000);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  };

  if (loadingModules) {
    return (
      <FormSection title="Device Modules">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading modules...</span>
        </div>
      </FormSection>
    );
  }

  // Filter out modules that are marked as hidden in productConfig
  const visibleModules = deviceModules.filter(module => {
    if (!productConfig || !productConfig.assignedModules) {
      return true; // If no productConfig, show all modules
    }
    const moduleName = module.module_type || 'Unknown Module';
    const productModule = productConfig.assignedModules.find(pm => pm.name === moduleName);
    // Show module if it's not found in productConfig (legacy) or if it's not hidden
    return !productModule || !productModule.hidden;
  });

  if (visibleModules.length === 0) {
    return (
      <FormSection title="Device Modules">
        <div className="text-center py-3 text-gray-500">No modules found</div>
      </FormSection>
    );
  }

  return (
    <FormSection title="Device Modules">
      {/* Floating toast: must not take layout space, otherwise the module list jumps when it disappears */}
      {repairSuccess && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm pointer-events-none">
          <AlertMessage type="success" message={repairSuccess} className="!mb-0 shadow-lg" />
        </div>
      )}
      <div className="space-y-2">
        {visibleModules.map((module, index) => {
          const moduleName = module.module_type || 'Unknown Module';
          const isRepairFormOpen = activeRepairModule === moduleName;
          const isExchangeFormOpen = activeExchangeModule === moduleName;
          const isEditFormOpen = activeEditModule === moduleName;
          const moduleRepairHistory = moduleRepairs[moduleName] || [];
          const moduleExchangeHistory = moduleExchanges[moduleName] || [];

          return (
            <div key={index} className={`border-b pb-2 last:border-b-0 px-2 py-1.5 rounded ${index % 2 === 1 ? 'bg-gray-50/50' : ''}`}>
              {/* Module Name Row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-gray-900 text-sm">
                    {moduleName}
                  </span>
                </div>
                {showActions && !isRepairFormOpen && !isExchangeFormOpen && !isEditFormOpen && (
                  <div className="flex flex-wrap gap-1.5">
                    {showEditButton && (
                      <Button
                        variant="secondary"
                        size="xs"
                        icon={Edit}
                        onClick={() => handleModuleAction('edit', module)}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="xs"
                      icon={Wrench}
                      onClick={() => handleModuleAction('repair', module)}
                    >
                      Repair
                    </Button>
                    <Button
                      variant="secondary"
                      size="xs"
                      icon={RefreshCw}
                      onClick={() => handleModuleAction('exchange', module)}
                    >
                      Exchange
                    </Button>
                  </div>
                )}
              </div>

              {/* Module Attributes Display */}
              {(() => {
                // Find the module ID from moduleIdMap
                const moduleId = moduleIdMap[moduleName];
                
                if (moduleId && moduleAttributesDefinition[moduleId]) {
                  const moduleAttrs = Array.isArray(moduleAttributesDefinition[moduleId]) 
                    ? moduleAttributesDefinition[moduleId] 
                    : [];
                  const hasAttributes = moduleAttrs.some(attr => {
                    const key = `${moduleId}_${attr.name}`;
                    const savedValue = moduleAttributeValues[key];
                    const fbValue = fallbackModuleValues[key];
                    return (savedValue && savedValue !== '') || (fbValue && fbValue !== '');
                  });
                  
                  if (hasAttributes) {
                    return (
                      <div className="ml-6 mt-1">
                        <div className="space-y-0.5">
                          {moduleAttrs
                            .map((attr) => {
                              const key = `${moduleId}_${attr.name}`;
                              const savedValue = moduleAttributeValues[key];
                              const effectiveValue = (savedValue && savedValue !== '') ? savedValue : fallbackModuleValues[key];
                              if (!effectiveValue || effectiveValue === '') return null;
                              
                              // Format boolean values as Yes/No
                              const displayValue = attr.type === 'boolean' 
                                ? (effectiveValue === 'true' ? 'Yes' : 'No') 
                                : effectiveValue;
                              
                              const simOwner = isIccidAttribute(attr.name)
                                ? getSimCardOwner(effectiveValue)
                                : null;

                              return (
                                <div
                                  key={attr.id}
                                  className={`flex gap-2 ${simOwner ? 'items-start' : 'items-center'}`}
                                >
                                  <span className="text-xs font-medium text-gray-600">{attr.name}:</span>
                                  {simOwner ? (
                                    <SimCardBadge iccid={effectiveValue} />
                                  ) : (
                                    <span className="text-xs text-gray-900">{displayValue}</span>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              })()}

              {/* Previous Repairs and Exchanges - Mixed and sorted by time */}
              {(() => {
                // Combine repairs and exchanges
                const repairsWithType = moduleRepairHistory.map(item => ({ ...item, actionType: 'repair' }));
                const exchangesWithType = moduleExchangeHistory.map(item => ({ ...item, actionType: 'exchange' }));
                const combined = [...repairsWithType, ...exchangesWithType];
                
                // Sort by timestamp descending (newest first)
                combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                
                return combined.length > 0 && !isRepairFormOpen && !isExchangeFormOpen && !isEditFormOpen && (
                  <div className="mt-2 ml-6 space-y-1">
                    {combined.map((item) => {
                      const details = typeof item.details === 'string' ? JSON.parse(item.details) : (item.details || {});
                      const isExchange = item.actionType === 'exchange';
                      const repairType = details.repair_type || '';
                      const repairTypeLabel = {
                        'paid': 'Paid',
                        'free_of_charge': 'Free',
                        'warranty': 'Warranty'
                      }[repairType] || (repairType ? repairType : '');
                      const descriptionField = isExchange ? details.exchange_description : details.repair_description;
                      const description = descriptionField && descriptionField.trim().length > 0
                        ? descriptionField.trim()
                        : '';
                      const repairId = details.repair_id ? Number(details.repair_id) : null;
                      const actionLabel = isExchange ? 'Exchanged' : 'Repaired';
                      const hasRepairId = !!repairId;
                      const linkHref = hasRepairId ? `/repairs/${repairId}/view` : null;

                      const isEditingThis = editHistory && editHistory.id === item.id && editHistory.type === item.actionType;

                      return (
                        <div key={`${item.actionType}-${item.id}`}>
                        <div className="text-xs text-gray-700 flex items-center justify-between group">
                          <div className="flex-1">
                            {hasRepairId ? (
                              <>
                                <a
                                  href={linkHref}
                                  className="text-naviter-blue font-semibold hover:underline"
                                >
                                  Repair #{repairId}
                                </a>
                                <span>: {item.performed_by_name || 'Unknown user'}</span>
                              </>
                            ) : (
                              <span>{item.performed_by_name || 'Unknown user'}</span>
                            )}
                            <span className="mx-1">•</span>
                            <span className={isExchange ? 'text-purple-600 font-medium' : 'text-gray-900'}>
                              {actionLabel}
                            </span>
                            {repairTypeLabel && (
                              <>
                                <span className="mx-1">•</span>
                                <span className="text-gray-900">{repairTypeLabel}</span>
                              </>
                            )}
                            {description && (
                              <>
                                <span className="mx-1">•</span>
                                <span className="text-gray-800">{description}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* Legacy rows live only in repair_activity: nothing to edit, but they can be removed */}
                            {!item.legacy && (
                              <button
                                onClick={() => handleOpenHistoryEdit(item, details, moduleName)}
                                className="ml-2 p-1 text-gray-400 hover:text-naviter-blue"
                                title="Edit comment or repair type"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteConfirm({
                                id: item.id,
                                type: item.actionType,
                                moduleName,
                                legacy: !!item.legacy
                              })}
                              className="ml-2 p-1 text-gray-400 hover:text-red-500"
                              title="Delete this record"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        {isEditingThis && (
                          <div className="mt-1 mb-2 bg-gray-50/70 border border-gray-200 rounded p-2">
                            {editHistoryError && (
                              <AlertMessage type="error" message={editHistoryError} className="mb-2" />
                            )}
                            <div className="space-y-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {isExchange ? 'Exchange' : 'Repair'} Description
                                </label>
                                <textarea
                                  value={editHistory.description}
                                  onChange={(e) => setEditHistory(prev => ({ ...prev, description: e.target.value }))}
                                  rows={2}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Repair Type <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                  {[
                                    { value: 'paid', label: 'Paid' },
                                    { value: 'free_of_charge', label: 'Free of charge' },
                                    { value: 'warranty', label: 'Warranty' }
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => setEditHistory(prev => ({ ...prev, repair_type: opt.value }))}
                                      className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                        editHistory.repair_type === opt.value
                                          ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="primary"
                                  size="xs"
                                  onClick={handleSaveHistoryEdit}
                                  disabled={editHistorySaving}
                                >
                                  {editHistorySaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="xs"
                                  onClick={handleCloseHistoryEdit}
                                  disabled={editHistorySaving}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Repair Form */}
              {isRepairFormOpen && (
                <div className="mt-2 ml-6 bg-gray-50/70 border border-gray-200 rounded p-2">
                  {repairError && (
                    <AlertMessage type="error" message={repairError} className="mb-2" />
                  )}
                  
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Repair Description
                      </label>
                      <textarea
                        value={repairFormData.repair_description}
                        onChange={(e) => handleRepairFormChange('repair_description', e.target.value)}
                        placeholder="Describe the repair performed..."
                        rows={2}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Repair Type <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        {[
                          { value: 'paid', label: 'Paid' },
                          { value: 'free_of_charge', label: 'Free of charge' },
                          { value: 'warranty', label: 'Warranty' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleRepairFormChange('repair_type', opt.value)}
                            className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                              repairFormData.repair_type === opt.value
                                ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="xs"
                        onClick={() => handleSaveRepair(moduleName)}
                        disabled={repairSaving}
                      >
                        {repairSaving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="xs"
                        onClick={handleCloseRepairForm}
                        disabled={repairSaving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Exchange Form */}
              {isExchangeFormOpen && (
                <div className="mt-2 ml-6 bg-gray-50/70 border border-gray-200 rounded p-2">
                  {exchangeError && (
                    <AlertMessage type="error" message={exchangeError} className="mb-2" />
                  )}
                  
                  <div className="space-y-2">
                    {/* Exchange Mode Selector - only for Motherboard */}
                    {moduleName.toLowerCase().includes('motherboard') && (
                      <div className="pb-2 mb-1 border-b border-gray-200">
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                          Exchange Source
                        </label>
                        <div className="flex flex-col sm:flex-row gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleExchangeModeChange('from_retired')}
                            className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                              exchangeMode === 'from_retired'
                                ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            From Retired Device
                          </button>
                          <button
                            type="button"
                            onClick={() => handleExchangeModeChange('new_motherboard')}
                            className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                              exchangeMode === 'new_motherboard'
                                ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            New Motherboard
                          </button>
                        </div>
                      </div>
                    )}

                    {/* From Retired Device - donor selection */}
                    {moduleName.toLowerCase().includes('motherboard') && exchangeMode === 'from_retired' && (
                      <div className="pb-2 mb-1 border-b border-gray-200">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Select Retired Device <span className="text-red-500">*</span>
                        </label>
                        {retiredDonorsLoading ? (
                          <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-naviter-blue"></div>
                            Loading retired devices...
                          </div>
                        ) : retiredDonors.length === 0 ? (
                          <div className="text-xs text-gray-500 py-1">
                            No eligible retired devices found.
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              ref={donorSearchRef}
                              type="text"
                              value={selectedDonor ? `${selectedDonor.serial_number} (${selectedDonor.product_name || 'Unknown Product'})` : donorSearchTerm}
                              onChange={(e) => {
                                if (selectedDonor) {
                                  setSelectedDonor(null);
                                  setExchangeAttributeValues({});
                                }
                                setDonorSearchTerm(e.target.value);
                                setDonorDropdownOpen(true);
                              }}
                              onFocus={() => setDonorDropdownOpen(true)}
                              placeholder="Search by serial number or product..."
                              className={`w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent ${selectedDonor ? 'bg-blue-50 font-medium' : ''}`}
                            />
                            {selectedDonor && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedDonor(null);
                                  setDonorSearchTerm('');
                                  setExchangeAttributeValues({});
                                  setDonorDropdownOpen(true);
                                  donorSearchRef.current?.focus();
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                ×
                              </button>
                            )}
                            {donorDropdownOpen && !selectedDonor && (
                              <div
                                ref={donorDropdownRef}
                                className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto"
                              >
                                {filteredRetiredDonors
                                  .map(donor => (
                                    <button
                                      key={donor.id}
                                      type="button"
                                      onClick={() => {
                                        handleSelectDonor(donor);
                                        setDonorSearchTerm('');
                                        setDonorDropdownOpen(false);
                                      }}
                                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-naviter-blue hover:text-white transition-colors border-b border-gray-100 last:border-b-0"
                                    >
                                      <span className="font-medium">{donor.serial_number}</span>
                                      <span className="text-gray-500 ml-1">({donor.product_name || 'Unknown Product'})</span>
                                    </button>
                                  ))
                                }
                                {filteredRetiredDonors.length === 0 && (
                                  <div className="px-2 py-1.5 text-xs text-gray-500">No matching devices found</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {selectedDonor && repairData.serial_number && (
                          <div className="mt-1.5 space-y-1.5">
                            <div className="px-2 py-1.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                              Serial number will change from <strong>{repairData.serial_number}</strong> to <strong>{selectedDonor.serial_number}</strong>
                            </div>
                            {deviceProduct && selectedDonor.product_id && selectedDonor.product_id !== deviceProduct.id && (
                              <div className="px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800">
                                Donor is a different product (<strong>{selectedDonor.product_name || 'Unknown'}</strong> → <strong>{deviceProduct.name || 'this device'}</strong>).
                                Attributes were not auto-filled — verify motherboard compatibility and enter them manually.
                              </div>
                            )}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Old motherboard ({repairData.serial_number}) status <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setReplacedDeviceStatus('retired')}
                                  className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                    replacedDeviceStatus === 'retired'
                                      ? 'bg-gray-600 text-white border-gray-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Retired
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setReplacedDeviceStatus('wasted')}
                                  className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                    replacedDeviceStatus === 'wasted'
                                      ? 'bg-red-600 text-white border-red-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Trashed
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* New Motherboard - serial number input */}
                    {moduleName.toLowerCase().includes('motherboard') && exchangeMode === 'new_motherboard' && (
                      <div className="pb-2 mb-1 border-b border-gray-200">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          New Serial Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newSerialNumber}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewSerialNumber(val);
                            const serialAttr = exchangeAttributesDefinition.find(a => a.name.toLowerCase().includes('serial number'));
                            if (serialAttr) {
                              setExchangeAttributeValues(prev => ({ ...prev, [serialAttr.name]: val.trim() }));
                            }
                          }}
                          placeholder="Enter new serial number (e.g., NAV-180200)..."
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                        />
                        {newSerialNumber.trim() && repairData.serial_number && (
                          <div className="mt-1.5 space-y-1.5">
                            <div className="px-2 py-1.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                              Serial number will change from <strong>{repairData.serial_number}</strong> to <strong>{newSerialNumber.trim()}</strong>.
                              A new device will be created for <strong>{repairData.serial_number}</strong>.
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Old motherboard ({repairData.serial_number}) status <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setReplacedDeviceStatus('retired')}
                                  className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                    replacedDeviceStatus === 'retired'
                                      ? 'bg-gray-600 text-white border-gray-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Retired
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setReplacedDeviceStatus('wasted')}
                                  className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                    replacedDeviceStatus === 'wasted'
                                      ? 'bg-red-600 text-white border-red-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Trashed
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Module Attributes - shown first */}
                    {Array.isArray(exchangeAttributesDefinition) && exchangeAttributesDefinition.length > 0 && (
                      <div className="space-y-1.5 pb-2 mb-2 border-b border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          New Module Attributes
                        </h4>
                        {exchangeAttributesDefinition
                          .filter(attr => !attr.hidden)
                          .map((attr) => {
                            const isSerialAttr = attr.name.toLowerCase().includes('serial number');
                            const isAutoFilled = isSerialAttr;
                            return (
                            <div key={attr.id}>
                              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                {attr.name}
                                {attr.required ? <span className="text-red-500">*</span> : ''}
                                {isAutoFilled && <span className="text-xs text-gray-400 ml-1">(auto)</span>}
                              </label>
                              {attr.type === 'boolean' ? (
                                <div className="flex gap-2">
                                  {[
                                    { value: 'true', label: 'Yes' },
                                    { value: 'false', label: 'No' }
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => handleModuleAttributeChange(attr.name, opt.value)}
                                      className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                        (exchangeAttributeValues[attr.name] || '') === opt.value
                                          ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              ) : attr.type === 'dropdown' ? (
                                attr.options && Array.isArray(attr.options) && attr.options.length <= 5 ? (
                                  <div className="flex gap-2">
                                    {attr.options.map((option, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleModuleAttributeChange(attr.name, option)}
                                        className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                          (exchangeAttributeValues[attr.name] || '') === option
                                            ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                      >
                                        {option}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <select
                                    value={exchangeAttributeValues[attr.name] || ''}
                                    onChange={(e) => handleModuleAttributeChange(attr.name, e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                                  >
                                    <option value="">Select...</option>
                                    {attr.options && Array.isArray(attr.options) && attr.options.map((option, idx) => (
                                      <option key={idx} value={option}>{option}</option>
                                    ))}
                                  </select>
                                )
                              ) : (
                                <input
                                  type={attr.type === 'number' ? 'number' : 'text'}
                                  value={exchangeAttributeValues[attr.name] || ''}
                                  onChange={(e) => !isAutoFilled && handleModuleAttributeChange(attr.name, e.target.value)}
                                  readOnly={isAutoFilled}
                                  placeholder={isAutoFilled ? 'Auto-filled from selection above' : `Enter ${attr.name.toLowerCase()}...`}
                                  className={`w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent ${isAutoFilled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                                />
                              )}
                            </div>
                            );
                          })}
                      </div>
                    )}
                    
                    {/* Exchange Description */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">
                        Exchange Description
                      </label>
                      <textarea
                        value={exchangeFormData.exchange_description}
                        onChange={(e) => handleExchangeFormChange('exchange_description', e.target.value)}
                        placeholder="Describe the module exchange and reason..."
                        rows={2}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">
                        Repair Type <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        {[
                          { value: 'paid', label: 'Paid' },
                          { value: 'free_of_charge', label: 'Free of charge' },
                          { value: 'warranty', label: 'Warranty' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleExchangeFormChange('repair_type', opt.value)}
                            className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                              exchangeFormData.repair_type === opt.value
                                ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="xs"
                        onClick={() => handleSaveExchange(moduleName)}
                        disabled={exchangeSaving}
                      >
                        {exchangeSaving ? 'Saving...' : 'Save Exchange'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="xs"
                        onClick={handleCloseExchangeForm}
                        disabled={exchangeSaving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Form */}
              {isEditFormOpen && (() => {
                const moduleId = moduleIdMap[moduleName];
                const moduleAttrs = moduleId ? (moduleAttributesDefinition[moduleId] || []) : [];
                
                return (
                  <div className="mt-2 ml-6 bg-gray-50/70 border border-gray-200 rounded p-2">
                    {editError && (
                      <AlertMessage type="error" message={editError} className="mb-2" />
                    )}
                    
                    <div className="space-y-2">
                      {moduleAttrs.length === 0 ? (
                        <div className="text-xs text-gray-500">No attributes available for this module</div>
                      ) : (
                        <>
                          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                            Module Attributes
                          </h4>
                          {moduleAttrs
                            .filter(attr => !attr.hidden)
                            .map((attr) => {
                              const key = `${moduleId}_${attr.name}`;
                              return (
                                <div key={attr.id}>
                                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                    {attr.name}
                                    {attr.required ? <span className="text-red-500">*</span> : ''}
                                  </label>
                                  {attr.type === 'boolean' ? (
                                    <div className="flex gap-2">
                                      {[
                                        { value: 'true', label: 'Yes' },
                                        { value: 'false', label: 'No' }
                                      ].map(opt => (
                                        <button
                                          key={opt.value}
                                          type="button"
                                          onClick={() => handleModuleEditValueChange(key, opt.value)}
                                          className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                            (moduleEditValues[key] || 'false') === opt.value
                                              ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                          }`}
                                        >
                                          {opt.label}
                                        </button>
                                      ))}
                                    </div>
                                  ) : attr.type === 'dropdown' ? (
                                    attr.options && Array.isArray(attr.options) && attr.options.length <= 5 ? (
                                      <div className="flex gap-2">
                                        {attr.options.map((option, idx) => (
                                          <button
                                            key={idx}
                                            type="button"
                                            onClick={() => handleModuleEditValueChange(key, option)}
                                            className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                              (moduleEditValues[key] || '') === option
                                                ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                          >
                                            {option}
                                          </button>
                                        ))}
                                      </div>
                                    ) : (
                                      <select
                                        value={moduleEditValues[key] || ''}
                                        onChange={(e) => handleModuleEditValueChange(key, e.target.value)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                                      >
                                        <option value="">Select...</option>
                                        {attr.options && Array.isArray(attr.options) && attr.options.map((option, idx) => (
                                          <option key={idx} value={option}>{option}</option>
                                        ))}
                                      </select>
                                    )
                                  ) : attr.type === 'date' ? (
                                    <input
                                      type="date"
                                      value={moduleEditValues[key] || ''}
                                      onChange={(e) => handleModuleEditValueChange(key, e.target.value)}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                                    />
                                  ) : attr.type === 'number' ? (
                                    <input
                                      type="number"
                                      value={moduleEditValues[key] || ''}
                                      onChange={(e) => handleModuleEditValueChange(key, e.target.value)}
                                      placeholder={`Enter ${attr.name.toLowerCase()}...`}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={moduleEditValues[key] || ''}
                                      onChange={(e) => handleModuleEditValueChange(key, e.target.value)}
                                      placeholder={`Enter ${attr.name.toLowerCase()}...`}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-naviter-blue focus:border-transparent"
                                    />
                                  )}
                                </div>
                              );
                            })}
                        </>
                      )}
                      
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => handleSaveModuleAttributes(moduleName)}
                          disabled={editSaving}
                        >
                          {editSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={handleCloseEditForm}
                          disabled={editSaving}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-md mx-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Delete {deleteConfirm.type === 'exchange' ? 'Exchange' : 'Repair'} Record
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Are you sure you want to delete this {deleteConfirm.type === 'exchange' ? 'exchange' : 'repair'} record 
              for <span className="font-medium">{deleteConfirm.moduleName}</span>?
            </p>
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
              This action cannot be undone. The record will be permanently removed from history.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="xs"
                onClick={handleDeleteModuleHistory}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </FormSection>
  );
};

export default DeviceModules;
